/* eslint-disable no-undef */

class ProductVideos extends HTMLElement {
  constructor () {
    super()
    this.currentVideoKey = null
    this.videoData = new Map()
    this.activeButton = null
  }

  connectedCallback () {
    this.initializeComponent()
  }

  initializeComponent () {
    const videoItems = this.querySelectorAll('.product-videos__item')
    videoItems.forEach((item, index) => {
      const videoKey = item.dataset.videoKey
      const block = this.getBlockDataByIndex(index)

      if (videoKey && block) {
        this.videoData.set(videoKey, {
          key: videoKey,
          title: block.title,
          runtime: block.runtime,
          videoSrc: block.videoSrc,
          videoImage: block.videoImage,
          element: item,
        })
      }
    })

    this.setupVideoItemListeners()

    if (videoItems.length > 0) {
      const firstVideoKey = videoItems[0].dataset.videoKey
      this.setCurrentVideo(firstVideoKey, false)
      this.setActiveItem(videoItems[0])
    }

    this.setupVideoEventListeners()
  }

  getBlockDataByIndex (index) {
    const item = this.querySelectorAll('.product-videos__item')[index]
    if (!item) return null

    return {
      title: item.querySelector('.product-videos__item-title').textContent.trim(),
      runtime: item.querySelector('.product-videos__item-runtime').textContent.trim(),
      videoSrc: item.dataset.videoSrc || '',
      videoImage: item.dataset.videoImage || '',
    }
  }

  setupVideoItemListeners () {
    const videoItems = this.querySelectorAll('.product-videos__item')

    videoItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault()
        const videoKey = item.dataset.videoKey
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        if (videoKey === this.currentVideoKey) {
          this.toggleCurrentVideo()
        } else if (isMobile) {
          this.switchToVideoMobile(videoKey, item)
        } else {
          this.setActiveItem(item)
          this.switchToVideo(videoKey, true)
        }
      })
    })
  }

  setupVideoEventListeners () {
    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) return

    videoPlayer.addEventListener('videoStateChange', (e) => {
      this.updateControlIcons(e.detail.state)
    })
  }

  setCurrentVideo (videoKey, autoPlay = false) {
    const videoData = this.videoData.get(videoKey)
    if (!videoData) {
      return
    }

    this.currentVideoKey = videoKey
    const videoPlayer = this.querySelector('product-video-player')

    if (!videoPlayer) {
      return
    }

    const isInitialLoad = !videoPlayer.videoPlayer?.hasBeenPlayed &&
                         videoPlayer.dataset.source === videoData.videoSrc &&
                         !videoPlayer.videoPlayer?.videoLoaded

    if (isInitialLoad) {
      if (videoData.videoImage) {
        const posterImg = videoPlayer.querySelector('.video-poster')
        if (posterImg) {
          posterImg.src = videoData.videoImage
          posterImg.alt = videoData.title
        }
      }

      if (videoPlayer.videoPlayer) {
        videoPlayer.videoPlayer.showPlayState()
        videoPlayer.classList.add('video-not-started')
      }
    } else {
      videoPlayer.switchVideo(videoData.videoSrc, videoData.videoImage, videoData.title)
    }

    if (autoPlay) {
      this.loadAndPlayVideo(videoPlayer, { muteOnMobile: false })
    }

    this.updateControlIcons()
  }

  loadAndPlayVideo (videoPlayer, { muteOnMobile = false } = {}) {
    if (typeof videoPlayer.loadVideo !== 'function') {
      return
    }

    const video = videoPlayer.querySelector('video')
    if (muteOnMobile && video) {
      video.muted = true
    }

    if (videoPlayer.videoPlayer) {
      videoPlayer.videoPlayer.shouldAutoPlayAfterLoad = true
    }

    videoPlayer.loadVideo()

    const waitForPlay = () => {
      if (videoPlayer.videoLoaded) {
        if (typeof videoPlayer.playVideo === 'function') {
          videoPlayer.playVideo()
        }
        return
      }

      if (!videoPlayer.isLoading) {
        if (videoPlayer.videoPlayer) {
          videoPlayer.videoPlayer.shouldAutoPlayAfterLoad = true
        }
        videoPlayer.loadVideo()
      }

      setTimeout(waitForPlay, 100)
    }

    setTimeout(waitForPlay, 100)
  }

  switchToVideo (videoKey, autoPlay = false) {
    this.setCurrentVideo(videoKey, autoPlay)
  }

  switchToVideoMobile (videoKey, item) {
    const videoData = this.videoData.get(videoKey)
    if (!videoData) {
      return
    }

    this.currentVideoKey = videoKey
    const videoPlayer = this.querySelector('product-video-player')

    if (!videoPlayer) {
      return
    }

    videoPlayer.switchVideo(videoData.videoSrc, videoData.videoImage, videoData.title)
    this.setActiveItem(item)
    this.updateControlIcons('loading')
    this.loadAndPlayVideo(videoPlayer, { muteOnMobile: true })
  }

  toggleCurrentVideo () {
    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) {
      return
    }

    if (!videoPlayer.videoLoaded && !videoPlayer.isLoading) {
      this.loadAndPlayVideo(videoPlayer)
    } else if (videoPlayer.videoLoaded) {
      if (videoPlayer.isPlaying) {
        if (typeof videoPlayer.pauseVideo === 'function') {
          videoPlayer.pauseVideo()
        }
      } else if (typeof videoPlayer.playVideo === 'function') {
        videoPlayer.playVideo()
      }
    }
  }

  setActiveItem (item) {
    const allItems = this.querySelectorAll('.product-videos__item')
    allItems.forEach(i => {
      i.classList.remove('product-videos__item--active')
      i.setAttribute('aria-pressed', 'false')
    })

    item.classList.add('product-videos__item--active')
    item.setAttribute('aria-pressed', 'true')
    this.activeButton = item
  }

  resetAllItemIcons () {
    this.querySelectorAll('.product-videos__item').forEach(item => {
      item.querySelector('.product-videos__item-icon-play')?.classList.remove('hidden')
      item.querySelector('.product-videos__item-icon-pause')?.classList.add('hidden')
      item.querySelector('.product-videos__item-icon-loading')?.classList.add('hidden')
    })
  }

  updateControlIcons (state = null) {
    if (!this.currentVideoKey || !this.activeButton) return

    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) return

    this.resetAllItemIcons()

    const activeItem = this.activeButton
    const playIcon = activeItem.querySelector('.product-videos__item-icon-play')
    const pauseIcon = activeItem.querySelector('.product-videos__item-icon-pause')
    const loadingIcon = activeItem.querySelector('.product-videos__item-icon-loading')

    playIcon?.classList.add('hidden')

    const currentState = state || (
      videoPlayer.isLoading
        ? 'loading'
        : videoPlayer.isPlaying ? 'playing' : 'ready'
    )

    switch (currentState) {
      case 'loading':
        loadingIcon?.classList.remove('hidden')
        break
      case 'playing':
        pauseIcon?.classList.remove('hidden')
        break
      default:
        playIcon?.classList.remove('hidden')
        break
    }
  }

  disconnectedCallback () {}
}

if (!customElements.get('product-videos')) {
  customElements.define('product-videos', ProductVideos)
}
